import {faArrowLeft} from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import {faMessages} from "@fortawesome/pro-solid-svg-icons/faMessages";
import {DevTool} from "@hookform/devtools";
import {AddressItemType, BatchAddressType, PostageUsageType} from "@twigsby/client-data";
import {
    Button,
    ButtonSize,
    ColourOutline,
    ColourSolid,
    ContentBlock,
    ContentLoader,
    FlexAlign,
    FlexGap,
    FlexJustify,
    FullWidthSidebar,
    ItemRowHeader,
    Pill,
    StackHorizontal,
    StackVertical,
    useLayout,
    useTranslation
} from "@twigsby/frontend-framework";
import {FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {FormatLocaleDate} from "@twigsby/helpers";
import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {AddressControl} from "../../Components/AddressControl";
import {BatchBatteryDeclaration} from "../../Components/ForwardingBatches/BatchSections/BatchBatteryDeclaration";
import {BatchContentsDeclaration} from "../../Components/ForwardingBatches/BatchSections/BatchContentsDeclaration";
import {BatchDetails} from "../../Components/ForwardingBatches/BatchSections/BatchDetails";
import {BatchDocuments} from "../../Components/ForwardingBatches/BatchSections/BatchDocuments";
import {BatchExportDetails} from "../../Components/ForwardingBatches/BatchSections/BatchExportDetails";
import {BatchLegal} from "../../Components/ForwardingBatches/BatchSections/BatchLegal";
import {BatchMailItems} from "../../Components/ForwardingBatches/BatchSections/BatchMailItems";
import {BatchNotes} from "../../Components/ForwardingBatches/BatchSections/BatchNotes";
import {BatchSignature} from "../../Components/ForwardingBatches/BatchSections/BatchSignature";
import {HarmonyCodeInfo} from "../../Components/ForwardingBatches/Controls/HarmonyCodeInfo";
import MailboxNavigation from "../../Components/Mail/MailboxNavigation";
import {PostageControl} from "../../Components/PostageControl";
import {useForwardingBatchProvider} from "../../Providers/ForwardingBatchProvider";
import {useSite} from "../../Providers/SiteInterfaceProvider";
import {BatchSectionType, RenderBatchSectionType} from "../../Types/ForwardingBatchTypes";

const DefaultBatchSections: Array<BatchSectionType> = [
    {
        sectionId: 0,
        name: 'Batch Details',
    },
    {
        sectionId: 1,
        name: 'Item Details',
        description: 'Please ensure you tell us about everything your letters/parcels contain. You can add additional rows using the "Add Contents" button'
    },
    {
        sectionId: 2,
        name: 'Contents Declaration',
        description: 'In the interest of safety to both UK Postbox And postal/courier staff, please tell us if your package/s contains any of the following. This information will also be used to ensure compliance with national, and international, transport regulations in relation to prohibited and dangerous goods.',
        hideForDocuments: true
    },
    {
        sectionId: 3,
        name: 'Battery Declaration',
        description: 'This shipment contains cells or batteries in the following configuration:',
        hideForNonLithiumBatteries: true
    },
    {
        sectionId: 4,
        name: 'Export Details',
        hideForDomestic: true
    },
    {
        sectionId: 5,
        name: 'Notes',
        description: 'If you\'d like to include any notes to the mailroom, please add them in the field below.',
        isOptional: true
    },
    {
        sectionId: 6,
        name: 'Documents',
        description: 'You can provide us with documents that will help us ship your batch.',
        isOptional: true
    },
    {
        sectionId: 7,
        name: 'Legal',
        description: 'By forwarding these items, you state that:'
    },
    {
        sectionId: 8,
        name: 'Digital Signature',
        hideForDomestic: true
    },
]

const ForwardingBatch = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { isMedium, isLarge } = useLayout();
    const { updatePageMeta, currentLocale, UpdateSiteContact } = useSite();

    const {
        addressUpdating,
        postageUpdating,
        currentBatchData,
        currentBatchError,
        currentBatchStatus,
        documentsOnly,
        showHarmony,
        showCommercialInvoice,
        showCn22,
        containsLithiumBatteries,
        ValidateForwardingBatch,
        UpdateAssignedAddress,
        UpdateAssignedPostage
    } = useForwardingBatchProvider();

    const formMethods = useForm({
        defaultValues: {
            addressId: currentBatchData?.address.addressId,
            postageId: currentBatchData?.postageOption.id,
        },
        mode: "onChange"
    });

    const [ activeAddressDetails, setActiveAddressDetails ] = useState<AddressItemType | null>(null);

    const statusColour = currentBatchData?.status?.title === 'Forwarded' ? ColourOutline.Success : currentBatchData?.status?.title === 'Pending' ? ColourOutline.Warning : ColourOutline.Alert;
    const mailBoxNavigationRef = useRef<any>();

    const batchReadOnly = currentBatchData?.locked || currentBatchData?.status?.id && [2, 99].includes(currentBatchData?.status?.id) || false;
    const batchComplete = currentBatchData?.status?.id === 5;

    let displaySectionIndex = 0;

    const HandleAddressChanged = (address: AddressItemType) => {
        if(address.object) {
            setActiveAddressDetails(address);
            UpdateAssignedAddress(address.object as unknown as BatchAddressType);
        }
    }

    useEffect(() => {
        updatePageMeta({
                title: currentBatchData?.batchIdNo ? `${currentBatchData?.batchIdNo} - ${t('forwardingBatches.title')}` : t('forwardingBatches.title')
        });
    }, [currentLocale, currentBatchStatus, currentBatchData]);

    return (
        <FullWidthSidebar
            navigationComponent={
                <>
                    {isLarge && (
                        <MailboxNavigation
                            ref={mailBoxNavigationRef}
                            title={currentBatchData?.batchIdNo as string}
                        />
                    )}
                </>
            }
            extraContentClass={`flex-column forwarding-batch-container`}
        >

            {currentBatchStatus === "loading" ? (
                <>
                    <div className={'page-header'}>
                        <div className={'container-narrow'}>
                            <div className={'row'}>
                                <div className={'col-xs-1'}>
                                    <ContentLoader rounded={true} childCount={1}/>
                                </div>
                                <div className={'col-xs-2'}>
                                    <ContentLoader rounded={true} childCount={1}/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={'card p-md mb-xxl'}>
                        <div className={'container-narrow'}>
                            <div className={'row'}>
                                <div className={'col-xs-12 col-md-4'}>
                                    <ContentLoader rounded={true} childCount={1} childHeight={'4em'}/>
                                </div>
                                <div className={'col-xs-12 col-md-4'}>
                                    <ContentLoader rounded={true} childCount={1} childHeight={'4em'}/>
                                </div>
                                <div className={'col-xs-12 col-md-4'}>
                                    <ContentLoader rounded={true} childCount={1} childHeight={'4em'}/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={'column-adjuster pl-md pr-md'}>
                        <div className={'container-narrow'}>
                            <div className={'row'}>
                                <div className={'col-xs-12'}>
                                    <StackVertical gap={FlexGap.ExtraExtraLarge}>
                                        <ContentLoader rounded={true} childCount={1} childHeight={'12em'}/>
                                        <ContentLoader rounded={true} childCount={1} childHeight={'12em'}/>
                                        <ContentLoader rounded={true} childCount={1} childHeight={'12em'}/>
                                        <ContentLoader rounded={true} childCount={1} childHeight={'12em'}/>
                                        <ContentLoader rounded={true} childCount={1} childHeight={'12em'}/>
                                    </StackVertical>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : currentBatchStatus === "error" ? (
                <ContentBlock error={true} stacked={true} title={currentBatchError?.status as string}
                              content={currentBatchError?.data.error as string} extraClass={'m-md'}/>
            ) : (
                <>
                    <div className={'sticky'}>
                        <div className={`page-header ${currentBatchData?.status.description ? 'account-issue-highlight from-left show-issues' : ''}}`}>
                            <div className={'container-narrow'}>

                                <StackHorizontal gap={FlexGap.Small} align={FlexAlign.Center}
                                                 justify={FlexJustify.Between}>
                                    <Button title={t('actions.goBack')}
                                            onClick={() => navigate(-1)}
                                            iconOnly={true}
                                            icon={faArrowLeft}/>

                                    <StackVertical gap={FlexGap.ExtraSmall}>
                                        <h5 className={`m-0 ${currentBatchData?.status.description ? 'text-white' : ''}`}>{currentBatchData?.batchIdNo}</h5>

                                        {currentBatchData?.status.description && (
                                            <>
                                                <h6 className={'text-white'}>{currentBatchData.status.description}</h6>
                                                <h6 className={'m-0 text-white'} dangerouslySetInnerHTML={{
                                                    __html: currentBatchData.status.adminNotes as string
                                                }} />
                                            </>
                                        )}

                                        {!currentBatchData?.status.description && (
                                            <StackHorizontal gap={FlexGap.ExtraSmall}>
                                                <Pill
                                                    content={t('status.dynamicType', {action: currentBatchData?.status?.title })}
                                                    colour={statusColour}
                                                />
                                                <Pill
                                                    content={currentBatchData?.lastChanged ? t('forwardingBatches.labels.lastChanged', { date: FormatLocaleDate(currentBatchData?.lastChanged, currentLocale) }) : ''}
                                                    colour={ColourOutline.Secondary}
                                                />
                                            </StackHorizontal>
                                        )}
                                    </StackVertical>

                                    {!isMedium && (
                                        <div className={'mail-item-menu ml-a'}>
                                            <Button
                                                title={'Item Actions'}
                                                squared={true}
                                                icon={faMessages}
                                                iconOnly
                                                showArrow={true}
                                            />
                                        </div>
                                    )}

                                    {isMedium && (
                                        <StackHorizontal gap={FlexGap.Small} justify={FlexJustify.End}>
                                            <Button
                                                title={'Message Us About this Item'}
                                                onClick={() => UpdateSiteContact(true, 3, undefined, `With regards to forwarding batch: ${currentBatchData?.batchIdNo}`)}
                                                squared={true}
                                                icon={faMessages}
                                                colour={currentBatchData?.status.description ? ColourSolid.Warning: ColourOutline.Secondary}/>
                                        </StackHorizontal>
                                    )}
                                </StackHorizontal>
                            </div>
                        </div>
                    </div>

                    <FormProvider {...formMethods}>

                        <ItemRowHeader extraClass={'mail-item-toolbar p-md'}>
                            <div className={'container-narrow'}>
                                <div className={'row'}>
                                    <div className={'col-xs-12 col-md-4'}>
                                        <StackVertical>
                                            <div className="h6 mb-xs">Forwarding To:</div>
                                            {(batchComplete || batchReadOnly) ? (
                                                <span>{currentBatchData?.address.formatted}</span>
                                            ) : (
                                                <AddressControl
                                                    name={'addressId'}
                                                    showCreateAddressOptions={!batchComplete}
                                                    forwardItemCallback={(item) => HandleAddressChanged(item)}
                                                    minimal={true}
                                                    defaultValues={currentBatchData?.address.addressId !== -1 ? [currentBatchData?.address.addressId] : undefined}
                                                    extraForwardingAddressData={currentBatchData?.address}
                                                    singleSelection={true}
                                                    loading={addressUpdating}
                                                />
                                            )}
                                        </StackVertical>
                                    </div>
                                    <div className={'col-xs-12 col-md-4'}>
                                        <StackVertical>
                                            <div className="h6 mb-xs">Postage Service Requested:</div>
                                            {(batchComplete || batchReadOnly) ? (
                                                <span>{currentBatchData?.postageOption.value}</span>
                                            ) : (
                                                <PostageControl
                                                    name={'postageId'}
                                                    postageType={PostageUsageType.Forwarding}
                                                    activeAddress={activeAddressDetails}
                                                    forwardItemCallback={(item) => UpdateAssignedPostage(item)}
                                                    minimal={true}
                                                    defaultValues={currentBatchData?.postageOption.id !== -1 ? [currentBatchData?.postageOption.id] : undefined}
                                                    singleSelection={true}
                                                    loading={postageUpdating}
                                                />
                                            )}


                                        </StackVertical>
                                    </div>
                                    <div className={'col-xs-12 col-md-4'}>
                                        {currentBatchData?.splits && (
                                            <StackVertical>
                                                <div className="h6 mb-xs">Tracking Details:</div>
                                                <StackVertical gap={FlexGap.ExtraSmall}>
                                                    {currentBatchData?.splits && currentBatchData?.splits.map((split, index) =>
                                                        <StackHorizontal gap={FlexGap.Small} align={FlexAlign.Center}
                                                                         justify={FlexJustify.Between}
                                                                         fillChildren={true}>
                                                            <div>Shipment {index + 1}</div>
                                                            <div>{split?.externalTrackingId}</div>
                                                            <Button
                                                                size={ButtonSize.Small}
                                                                title={'View Tracking'}
                                                                actionLink={{
                                                                    title: 'View Tracking',
                                                                    url: split.externalTrackingUrl,
                                                                    target: '_blank'
                                                                }}
                                                            />
                                                        </StackHorizontal>
                                                    )}
                                                </StackVertical>
                                            </StackVertical>
                                        )}

                                        {!!currentBatchData?.trackingNumber && currentBatchData?.trackingNumbers && (
                                            <StackVertical>
                                                <div className="h6 mb-xs sm-sub-heading">Tracking Details:</div>
                                                {!!currentBatchData?.trackingNumber && (
                                                    <div>{currentBatchData?.trackingNumber}</div>
                                                )}
                                                {currentBatchData?.trackingNumbers && (
                                                    <div>{currentBatchData?.trackingNumbers.toString()}</div>
                                                )}
                                            </StackVertical>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </ItemRowHeader>

                        <div className={'column-adjuster pl-md pr-md'}>
                            <div className={'container-narrow'}>
                                <div className={'row'}>
                                    <div className={'col-xs-12'}>
                                        <div className={'flex-column flex-gap-xl mt-xxl mb-xxl'}>
                                            {!batchComplete && !documentsOnly && showHarmony && (
                                                <HarmonyCodeInfo/>
                                            )}

                                            {DefaultBatchSections.map((section, index) => {
                                                if (section.sectionId === 8 && documentsOnly || section.sectionId === 8 && !showCommercialInvoice && !showCn22 && !containsLithiumBatteries) return null;
                                                if (section.sectionId === 4 && !showCommercialInvoice) return null;
                                                if (section.sectionId === 3 && documentsOnly || section.sectionId === 3 && !containsLithiumBatteries) return null;
                                                if (section.sectionId === 2 && documentsOnly) return null;
                                                if (section.sectionId === 0 && batchComplete) return null;

                                                displaySectionIndex = displaySectionIndex + 1;

                                                return (
                                                    <RenderBatchSection
                                                        key={`batchSection${index}`}
                                                        section={section}
                                                        sectionIndex={displaySectionIndex}
                                                        readOnly={batchReadOnly}
                                                        complete={batchComplete}
                                                    />
                                                )
                                            })}

                                            {!batchComplete && currentBatchData?.batchId && (
                                                <Button
                                                    title={'Forward Items'}
                                                    colour={ColourSolid.Success}
                                                    fullWidth={true}
                                                    disabled={batchReadOnly || !formMethods.formState.isValid}
                                                    onClick={() => ValidateForwardingBatch()}
                                                />
                                            )}

                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </FormProvider>
                </>
            )}
        </FullWidthSidebar>
    )
}

const RenderBatchSection = (props: RenderBatchSectionType) => {
    switch (props.section.sectionId) {
        case 0:
            return (
                <BatchDetails {...props} />
            )
        case 1:
            return (
                <BatchMailItems {...props} />
            )
        case 2:
            return (
                <BatchContentsDeclaration {...props} />
            )
        case 3:
            return (
                <BatchBatteryDeclaration {...props} />
            )
        case 4:
            return (
                <BatchExportDetails {...props} />
            )
        case 5:
            return (
                <BatchNotes {...props} />
            )
        case 6:
            return (
                <BatchDocuments {...props} />
            )
        case 7:
            return (
                <BatchLegal {...props} />
            )
        case 8:
            return (
                <BatchSignature {...props} />
            )
        default:
            return <></>
    }
}

export default ForwardingBatch
